import '../public/fonts/stylesheet.css'
import '../public/global.css'
import { LazyMotion, domAnimation, AnimatePresence } from 'motion/react'
import { AppProps } from 'next/app'
import Head from 'next/head'
import { Router } from 'next/router'
import { useEffect, useContext, useRef, ReactNode } from 'react'
import { SanityAnyPage } from '@data/sanity/queries/types/page'
import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import { pageTransitionSpeed } from '@lib/animate'
import { isBrowser } from '@lib/helpers'
import { LanguageContextProvider, Locale } from '@lib/language'
import { LayoutContextProvider } from '@lib/layout-context'
import { MetadataContextProvider } from '@lib/metadata'
import { BasePageProps } from '@lib/page'
import { PageHeroContextProvider } from '@lib/page-hero-context'
import { PageModuleContextProvider } from '@lib/page-module-context'
import PreviewProvider from '@lib/sanity/preview'
import { SeoContextProvider } from '@lib/seo-context'
import { SiteContext, SiteContextProvider } from '@lib/site-context'
import { StringsContextProvider } from '@lib/strings'
import RouteChangeProgressBar from '@components/route-change-progress-bar'
import { AnalyticsContextProvider } from '@lib/analytics/context'
import { getPageLocation } from '@lib/routes'
import { triggerGoogleTagManagerEvent } from '@lib/analytics/event'
import ExternalScripts from '@components/external-scripts'

interface TransitionOptions {
  shallow?: boolean
  locale?: string | false
  scroll?: boolean
}

interface NextHistoryState {
  url: string
  as: string
  options: TransitionOptions
}

type AppPageProps = BasePageProps & {
  site: SanitySiteFragment
  page: SanityAnyPage
}

type DefaultAppProps = AppProps<AppPageProps>

type CustomAppProps = DefaultAppProps & {
  pageProps: AppPageProps
}

type SiteProps = Pick<DefaultAppProps, 'router'> & {
  pageProps: AppPageProps
  children: ReactNode
}

/**
 * Add new position to scroll positions.
 */
const addScrollPosition = (
  positions: Record<string, number>,
  locale: Locale,
  url: string,
  position: number,
) => {
  const key = `${locale}:${url}`
  const alternativeKey = `${locale}:/${locale}${url.replace(/\/+$/g, '')}`

  return {
    ...positions,
    [key]: position,
    [alternativeKey]: position,
  }
}

/**
 * Router event handler hook.
 */
const useRouterEvents = (router: Router, locale: Locale) => {
  const { togglePageTransition, toggleMobileMenu } = useContext(SiteContext)

  const scrollPositions = useRef<Record<string, number>>({})
  const shouldScrollRestore = useRef(false)
  const isInitialLoad = useRef(true)

  useEffect(() => {
    // Prevent browser scroll restoration
    window.history.scrollRestoration = 'manual'
  }, [])

  useEffect(() => {
    function handleBeforeUnload(event: BeforeUnloadEvent) {
      if (!isInitialLoad.current) {
        // Save scroll position
        scrollPositions.current = addScrollPosition(
          scrollPositions.current,
          locale,
          router.asPath,
          window.scrollY,
        )
      }

      delete event['returnValue']
    }

    function handlePopState() {
      // Reload page instead of reverting state manually (see CasePageLink component)
      router.reload()
      togglePageTransition(true)
    }

    function handleRouteChangeStart(_: string, { shallow }: TransitionOptions) {
      toggleMobileMenu(false)

      if (!isInitialLoad.current) {
        // Save scroll position
        scrollPositions.current = addScrollPosition(
          scrollPositions.current,
          locale,
          router.asPath,
          window.scrollY,
        )
      }

      // Check if URL is changing
      if (!shallow) {
        togglePageTransition(true)
      }
    }

    function handleRouteChangeComplete(
      url: string,
      { shallow }: TransitionOptions,
    ) {
      // Wait for page transition to complete
      setTimeout(() => {
        togglePageTransition(false)
      }, pageTransitionSpeed)

      // Check if URL is changing
      if (!isInitialLoad.current && !shallow) {
        // Restore scroll position after route change completes
        const position = scrollPositions.current[`${locale}:${url}`]
        const top = position && shouldScrollRestore.current ? position : 0

        // Restore scroll position or set it to 0
        setTimeout(
          () => requestAnimationFrame(() => window.scrollTo({ top })),
          pageTransitionSpeed + 100,
        )

        shouldScrollRestore.current = false
      }

      isInitialLoad.current = false

      // Wait for document title to update, then trigger analytics event
      setTimeout(() => {
        triggerGoogleTagManagerEvent('page_view', {
          page_title: document.title,
          page_location: getPageLocation(),
        })
      }, pageTransitionSpeed + 101)
    }

    function handleRouteChangeError() {
      togglePageTransition(false)
    }

    function handleBeforePopState({ options }: NextHistoryState) {
      // Allow scroll position restoring
      shouldScrollRestore.current = true
      options.scroll = false

      return true
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('popstate', handlePopState)
    router.events.on('routeChangeStart', handleRouteChangeStart)
    router.events.on('routeChangeComplete', handleRouteChangeComplete)
    router.events.on('routeChangeError', handleRouteChangeError)
    router.beforePopState(handleBeforePopState)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('popstate', handlePopState)
      router.events.off('routeChangeStart', handleRouteChangeStart)
      router.events.off('routeChangeComplete', handleRouteChangeComplete)
      router.events.off('routeChangeError', handleRouteChangeError)
      router.beforePopState(() => true)
    }
  }, [locale, router, toggleMobileMenu, togglePageTransition])
}

/**
 * Keyboard focus state handler hook.
 */
const useKeyboardListener = () => {
  useEffect(() => {
    const handleKeyDown = ({ key }: KeyboardEvent) => {
      // Check if "tab" key was pressed
      if (key === 'Tab' && isBrowser) {
        document.body.classList.add('is-tabbing')
        window.removeEventListener('keydown', handleKeyDown)
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])
}

const Site = ({ pageProps, router, children }: SiteProps) => {
  const { isPageTransition } = useContext(SiteContext)

  // Handle router events & scroll position restoration
  useRouterEvents(router, pageProps.locale)

  // Handle keyboard navigation
  useKeyboardListener()

  return (
    <LazyMotion features={domAnimation}>
      <ExternalScripts publicSettings={pageProps?.site?.settings} />

      {isPageTransition && (
        <Head>
          {pageProps.site.siteStrings?.loadingPageTitle && (
            <title>{pageProps.site.siteStrings.loadingPageTitle}</title>
          )}
        </Head>
      )}

      <RouteChangeProgressBar />

      <AnimatePresence
        mode="wait"
        onExitComplete={() => {
          document.body.classList.remove('overflow-hidden')
          document.body.classList.remove('!overflow-hidden')
        }}
      >
        {children}
      </AnimatePresence>
    </LazyMotion>
  )
}

const CustomApp = ({ Component, pageProps, router }: CustomAppProps) => {
  if (!pageProps.site) {
    return <Component />
  }

  let app = (
    <StringsContextProvider site={pageProps.site}>
      <SiteContextProvider site={pageProps.site}>
        <SeoContextProvider site={pageProps.site} page={pageProps.page}>
          <MetadataContextProvider>
            <LanguageContextProvider
              locale={pageProps.locale}
              site={pageProps.site}
            >
              <LayoutContextProvider
                pageIsHeaderTransparent={
                  pageProps.page &&
                  'hasTransparentHeader' in pageProps.page &&
                  !!pageProps.page.hasTransparentHeader
                }
                pageHeaderBackgroundColor={
                  pageProps.page && 'headerBackgroundColor' in pageProps.page
                    ? pageProps.page.headerBackgroundColor
                    : undefined
                }
              >
                <PageHeroContextProvider modules={pageProps.page?.modules}>
                  <PageModuleContextProvider modules={pageProps.page?.modules}>
                    <AnalyticsContextProvider>
                      <Site pageProps={pageProps} router={router}>
                        <Component
                          key={router.asPath.split('?')[0]}
                          {...pageProps}
                        />
                      </Site>
                    </AnalyticsContextProvider>
                  </PageModuleContextProvider>
                </PageHeroContextProvider>
              </LayoutContextProvider>
            </LanguageContextProvider>
          </MetadataContextProvider>
        </SeoContextProvider>
      </SiteContextProvider>
    </StringsContextProvider>
  )

  if (pageProps.draftMode) {
    app = (
      <PreviewProvider token={pageProps.draftModeToken}>{app}</PreviewProvider>
    )
  }

  return app
}

export default CustomApp
